import { Box, Button, Chip, Typography } from "@mui/joy";
import { Container, Settings } from "lucide-react";
import type { PurchaseStrategy, SavedSupplierInfo } from "../types";

interface SelectedSuppliersDisplayProps {
    purchaseStrategy: PurchaseStrategy;
    selectedSupplier: { name: string; id: string } | null;
    savedSuppliersInfo: SavedSupplierInfo[];
    itemSupplierSelections: Record<string, string>;
    onClearSelection: () => void;
}

export function SelectedSuppliersDisplay({
    purchaseStrategy,
    selectedSupplier,
    savedSuppliersInfo,
    itemSupplierSelections,
    onClearSelection,
}: SelectedSuppliersDisplayProps) {
    const hasSelection =
        selectedSupplier?.name || Object.keys(itemSupplierSelections).length > 0 || savedSuppliersInfo.length > 0;

    if (!hasSelection) {
        return null;
    }

    return (
        <Box
            sx={{
                p: 2,
                borderRadius: "sm",
                bgcolor: "background.level1",
                border: "1px solid",
                borderColor: "divider",
            }}
        >
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                <Typography level="body-sm" sx={{ color: "text.primary", fontWeight: 600 }}>
                    Selected Suppliers
                </Typography>
                <Button
                    variant="outlined"
                    color="neutral"
                    size="sm"
                    onClick={onClearSelection}
                    startDecorator={<Settings size={16} />}
                >
                    {purchaseStrategy === "single" ? "Change Supplier" : "Change Selection"}
                </Button>
            </Box>

            {purchaseStrategy === "single" && selectedSupplier?.name && (
                <Box display="flex" alignItems="center" gap={1}>
                    <Chip variant="soft" size="md" color="primary" startDecorator={<Container size={16} />}>
                        {selectedSupplier.name}
                    </Chip>
                </Box>
            )}

            {purchaseStrategy === "multiple" && (
                <Box display="flex" flexDirection="column" gap={1.5}>
                    <Typography level="body-xs" sx={{ color: "text.secondary" }}>
                        {Object.keys(itemSupplierSelections).length > 0
                            ? `${Object.keys(itemSupplierSelections).length} product(s) distributed across ${
                                  Array.from(new Set(Object.values(itemSupplierSelections))).length
                              } supplier(s)`
                            : `${savedSuppliersInfo.length} supplier(s) selected - run comparison to assign products`}
                    </Typography>
                    <Box display="flex" gap={1} flexWrap="wrap">
                        {savedSuppliersInfo.length > 0
                            ? savedSuppliersInfo.map((supplierInfo) => {
                                  const itemCount = Object.values(itemSupplierSelections).filter(
                                      (name) => name === supplierInfo.name
                                  ).length;

                                  return (
                                      <Chip
                                          key={supplierInfo.id}
                                          size="sm"
                                          variant="soft"
                                          color={itemCount > 0 ? "primary" : "neutral"}
                                          sx={{
                                              fontSize: "0.75rem",
                                              fontWeight: itemCount > 0 ? 500 : 400,
                                          }}
                                      >
                                          {supplierInfo.name}: {itemCount} item{itemCount !== 1 ? "s" : ""}
                                      </Chip>
                                  );
                              })
                            : Array.from(new Set(Object.values(itemSupplierSelections))).map((supplierName) => {
                                  const itemCount = Object.values(itemSupplierSelections).filter(
                                      (name) => name === supplierName
                                  ).length;
                                  return (
                                      <Chip
                                          key={supplierName}
                                          size="sm"
                                          variant="soft"
                                          color="primary"
                                          sx={{ fontSize: "0.75rem" }}
                                      >
                                          {supplierName}: {itemCount} item{itemCount !== 1 ? "s" : ""}
                                      </Chip>
                                  );
                              })}
                    </Box>
                </Box>
            )}
        </Box>
    );
}
